import { useState } from 'react'
import { useFormatters } from '../../contexts/PreferencesContext'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps } from './types'
import { ChartWrapper } from './ChartWrapper'
import { PPVChartPanels, TopNSelector } from './PPVCharts'
import { CostHistoricalView } from './CostHistoricalView'
import { CostSubTabs } from './CostSubTabs'
import { buildCostDrillFilters, monthWithinPeriod, parseMonthToDateRange } from './costDrillFilters'
import { quickRangeLabel, type CostView } from './costViewRanges'
import { useDrilldownAction } from '../drilldown'
import { RankedBarList } from './charts/RankedBarList'
import { useSeriesToggle } from './charts/useSeriesToggle'
import { LegacyIncomingPerWeekChart } from './LegacyIncomingPerWeekChart'
import { CostWeeklyCharts } from './cost-weeks/CostWeeklyCharts'

interface CostTabProps extends TabProps {
  /** CR-8611: the Future/Historical sub-tab, owned by the page so the filter panel sees it. */
  costView?: CostView
  onCostViewChange?: (view: CostView) => void
}

export function CostTab({ data, filters, dataPeriod: _dataPeriod, orderType, costView, onCostViewChange }: CostTabProps) {
  const { formatCurrency, formatCompactCurrency, formatDate } = useFormatters()
  const [ownCostTab, setOwnCostTab] = useState<CostView>('future')
  const activeCostTab = costView ?? ownCostTab
  const setActiveCostTab = onCostViewChange ?? setOwnCostTab
  // CR-3848: Add state for Highest Value Suppliers top N selection
  const [topHighestValueSuppliers, setTopHighestValueSuppliers] = useState(5)
  const arrearsSeries = useSeriesToggle() // CR-1421 item 5: legend show/hide

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Drill-down filter objects, one per population (costDrillFilters.ts).
  const {
    scopeDrilldownFilters, drilldownFilters, costLine, incomingWindow,
    arrearsDrilldownFilters, ppvDrilldownFilters, ppvFutureDrilldownFilters, incomingDrilldownFilters,
    suppliersDrilldownFilters,
  } = buildCostDrillFilters(filters, orderType)

  // Handle chart click - open drill-down modal with month-specific data
  // CR-3918: Added 'ppv' metric type for PPV to Contract/Standard chart
  // CR-7270: `extra` carries per-chart drill params. A drill inherits the
  // dashboard's Historical/Live switch by default, which is right for a series
  // that FOLLOWS the switch (Historical Arrears Trend is the frozen month-end
  // value). A series that is live whatever the switch says has to declare it —
  // see the Open Arrears By Due Month call site below.
  const handleChartClick = (
    chartData: unknown,
    metricType: 'spend' | 'arrears' | 'ppv' | 'open_order_value',
    extra?: Record<string, string>,
  ) => {
    if (!chartData) return
    const data = chartData as { activePayload?: Array<{ payload?: { month?: string } }>; activeLabel?: string }
    let month: string | null = null
    if (data.activePayload && data.activePayload[0]) {
      month = data.activePayload[0].payload?.month || null
    } else if (data.activeLabel) {
      month = data.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    // Incoming by Month's bars bucket on the promise date (else the required
    // date) inside the Cost period (CR-8610), so a month's drill sends that
    // month, cut to the period, AS the incoming window: the one filter that
    // reads that same date. The dashboard's own dates (the required date, on
    // this metric) would select a different set.
    if (metricType === 'open_order_value') {
      openDrilldown(metricType, {
        ...scopeDrilldownFilters,
        ...costLine,
        ...(dateRange ? monthWithinPeriod(dateRange, incomingWindow) : {}),
        ...extra,
      }, month)
      return
    }
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
      ...costLine,
      ...extra,
    } : { ...drilldownFilters, ...costLine, ...extra }

    openDrilldown(metricType, chartFilters, month)
  }

  // CR-1421 A2: a tile shows its own figure or, before the Cost payload has
  // arrived, a dash -- never another figure (it used to fall back to GRN spend,
  // the historical arrears value and the page's line count whenever its own
  // figure was 0).
  const costLoaded = Boolean(data.costData)
  const tileMoney = (v: number) => (costLoaded ? formatCurrency(v) : '–')
  const tileCount = (v: number) => (costLoaded ? v.toLocaleString() : '–')

  // Get cost data from backend or use defaults
  const costData = data.costData || {
    total_incoming_value: 0,
    total_incoming_lines: 0,
    total_arrears_value: 0,
    total_arrears_lines: 0,
    total_suppliers: 0,
    incoming_by_week: [],
    arrears_by_supplier: [],
  }

  // Incoming by Month — 12 forward months of open-PO commitment by Latest
  // Supplier Promise Date (with fallback to delivery_date). Backend computes
  // this in costData.incoming_by_month so the Future view shows forward-
  // looking demand, not historical received GRN spend.
  const incomingByMonthValue = (costData.incoming_by_month || []) as Array<{ month: string; value: number; lines: number }>

  // CR-4211: Use real per-month line counts from API (falls back to even distribution)
  const incomingByMonthLines = incomingByMonthValue.map((item) => ({
    month: item.month,
    lines: item.lines ?? 0,
  }))

  // Arrears by suppliers data (value and count)
  // CR-3966: Keep full supplier name for drill-down filtering, truncate only for display via tick formatter
  const arrearsBySupplier = (costData.arrears_by_supplier || []).map((s: { supplier: string; arrears: number; count: number }) => ({
    supplier: s.supplier,
    arrears: s.arrears,
    count: s.count || 0,
  }))

  // Ranked-list rows for the arrears panel: by value, each row also carrying
  // the line count so one list answers both "who owes most" and "how many".
  const maxArrearsValue = Math.max(0, ...arrearsBySupplier.map(s => s.arrears || 0))
  const maxArrearsCount = Math.max(0, ...arrearsBySupplier.map(s => s.count || 0))
  // CR-1421 item 5: each supplier's share of ALL arrears lines (the Total Arrears
  // tile's count), as legacy's tooltip gave it (Dashboard.js:7686-7703).
  const allArrearsLines = costData.total_arrears_lines || 0
  const share = (n: number) => (allArrearsLines ? ` · ${Math.round((n / allArrearsLines) * 100)}% of arrears lines` : '')
  const arrearsRanked = [...arrearsBySupplier]
    .sort((a, b) => (b.arrears || 0) - (a.arrears || 0))
    .map(s => ({
      key: s.supplier,
      label: s.supplier,
      bars: [
        ...(arrearsSeries.isHidden('Arrears value') ? [] : [
          { color: '#ef4444', fraction: maxArrearsValue ? (s.arrears || 0) / maxArrearsValue : 0, label: formatCurrency(s.arrears || 0) },
        ]),
        ...(arrearsSeries.isHidden('Arrears PO lines') ? [] : [
          { color: '#3b82f6', fraction: maxArrearsCount ? (s.count || 0) / maxArrearsCount : 0, label: `${s.count || 0} lines${share(s.count || 0)}` },
        ]),
      ],
    }))

  // CR-5456: PPV chart data, aggregated server-side from persisted per-line
  // PPV (CR-5957). Undefined for supplier users — the backend gates it out,
  // so the PPV panels simply don't render on the supplier portal.
  const ppvData = data.costData?.ppv

  // CR-3848 / CR-5457: Highest Value Suppliers — prefer the server-side
  // top_suppliers_by_open_value aggregation. The legacy fallback walks
  // incoming_lines (a 100-row sample of full-line values), which drifted
  // from the open-order book the KPI tile shows because (a) the sample
  // truncated, (b) total_value was full line × qty even for partially
  // received lines so the chart effectively read closed/received PO
  // value (Sarah's "GRN data" symptom).
  const serverTop = (costData as {
    top_suppliers_by_open_value?: Array<{ name: string; value: number }>
  }).top_suppliers_by_open_value
  const highestValueSuppliers = (
    serverTop && serverTop.length > 0
      ? serverTop
      : (costData.incoming_lines || [] as Array<{ supplier: string; total_value: number }>)
        .reduce(
          (acc: Array<{ name: string; value: number }>, line: { supplier: string; total_value: number }) => {
            const existing = acc.find(s => s.name === line.supplier)
            if (existing) {
              existing.value += line.total_value
            } else {
              acc.push({ name: line.supplier, value: line.total_value })
            }
            return acc
          },
          [] as Array<{ name: string; value: number }>,
        )
  )
    .slice()
    .sort((a: { value: number }, b: { value: number }) => b.value - a.value)
    .slice(0, topHighestValueSuppliers)

  const maxHighestValue = Math.max(0, ...highestValueSuppliers.map((s: { value: number }) => s.value || 0))
  const highestValueRanked = highestValueSuppliers.map((s: { name: string; value: number }) => ({
    key: s.name,
    label: s.name,
    bars: [{ color: '#8b5cf6', fraction: maxHighestValue ? (s.value || 0) / maxHighestValue : 0, label: formatCurrency(s.value || 0) }],
  }))

  return (
    <div className="space-y-4">
      {/* Sub-tabs for Future and Historical */}
      <div className="bg-card rounded-lg shadow-sm">
        <CostSubTabs active={activeCostTab} onChange={setActiveCostTab} />
        <div className="p-6">
          {activeCostTab === 'future' && (
            <div className="space-y-6">
              {/* CR-5454: Cost Overview KPI cards.
                  - Visual: dropped the blue-on-blue inner panels (was
                    bg-blue-700 inside bg-blue-600 — Sarah called the
                    contrast "intrusive"). Outer card stays brand-blue;
                    inner stat panels switched to white/transparent with
                    a subtle border for legibility.
                  - Click-through: each card now opens the matching
                    drilldown ('open_order_value' for Incoming, 'arrears' for
                    Arrears, 'suppliers' for Total Suppliers) so the
                    headline figures can be validated, per the spec.
                  - Rework (QA 2026-07-20): "Total Incoming" was wired to the
                    'spend' drilldown (GoodsReceiptNote — already-delivered
                    goods), so the modal population didn't match the card's
                    "N PO Lines" count. total_incoming_value/lines are
                    computed from PurchaseOrderLine filtered to
                    status__in=['pending','partially_received']
                    (see backend/purchasing/views/po_dashboard_calculators/cost.py),
                    which is exactly what the 'open_order_value' drilldown
                    metric queries — switched to that. */}
              <div className="grid grid-cols-3 gap-6">
                <button
                  type="button"
                  onClick={() => openDrilldown('open_order_value', incomingDrilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors text-left"
                  title="Drill into incoming PO lines"
                >
                  <h3 className="text-lg font-medium mb-2 text-center">Total Incoming</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">Value</div>
                      <div className="text-xl font-bold">{tileMoney(costData.total_incoming_value)}</div>
                    </div>
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">No.of PO Lines</div>
                      <div className="text-xl font-bold">{tileCount(costData.total_incoming_lines)}</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => openDrilldown('arrears', arrearsDrilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors text-left"
                  title="Drill into arrears PO lines"
                >
                  <h3 className="text-lg font-medium mb-2 text-center">Total Arrears</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">Value</div>
                      <div className="text-xl font-bold">{tileMoney(costData.total_arrears_value)}</div>
                    </div>
                    <div className="bg-white/10 border border-white/30 p-3 rounded">
                      <div className="text-sm opacity-90">No.of PO Lines</div>
                      <div className="text-xl font-bold">{tileCount(costData.total_arrears_lines)}</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => openDrilldown('suppliers_with_open_pos', suppliersDrilldownFilters)}
                  className="bg-blue-600 text-white p-6 rounded-lg text-center hover:bg-blue-700 transition-colors"
                  title="Drill into suppliers with at least one open PO line"
                >
                  <h3 className="text-lg font-medium mb-2">Suppliers with Open POs</h3>
                  <div className="text-4xl font-bold mt-4">{tileCount(costData.total_suppliers)}</div>
                </button>
              </div>

              {/* Charts Row 1 - Incoming by Month (Value and Lines) */}
              <div className="grid grid-cols-2 gap-6">
                <ChartWrapper
                  title="Incoming by Month (Value)"
                  data={incomingByMonthValue}
                  exportFilename="incoming-by-month-value"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={incomingByMonthValue}
                      onClick={(chartData) => handleChartClick(chartData, 'open_order_value')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#3b82f6" name="Value" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="Incoming by Month (PO Lines)"
                  data={incomingByMonthLines}
                  exportFilename="incoming-by-month-lines"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={incomingByMonthLines}
                      onClick={(chartData) => handleChartClick(chartData, 'open_order_value')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="lines" fill="#10b981" name="PO Lines" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* CR-1421: the weekly charts look forward a page at a time, with payment terms
                  and a payments chart; schedule-line scopes keep the existing chart. */}
              <CostWeeklyCharts
                params={data.costParams}
                fallback={
                  <LegacyIncomingPerWeekChart
                    incomingByWeek={costData.incoming_by_week || []}
                    scopeDrilldownFilters={scopeDrilldownFilters}
                    costLine={costLine}
                  />
                }
              />

              {/* Charts Row 2 - Arrears By Supplier (value + line count, one ranked list) */}
              {/* CR-3966: drill-down passes the clicked supplier's name */}
              <ChartWrapper
                title="Arrears By Supplier"
                data={arrearsBySupplier}
                exportFilename="arrears-by-supplier"
                legend={[
                  { color: '#ef4444', label: 'Arrears value' },
                  { color: '#3b82f6', label: 'Arrears PO lines' },
                ]}
                visibleSeries={arrearsSeries.visibleSeries}
                onSeriesToggle={arrearsSeries.onSeriesToggle}
              >
                <RankedBarList
                  items={arrearsRanked}
                  valueWidth="11rem"
                  onSelect={(supplierName) => openDrilldown('arrears', {
                    ...arrearsDrilldownFilters,
                    supplier_name: supplierName,
                  }, supplierName)}
                />
              </ChartWrapper>

              {/* W3-T14 (FIX-04): the live backlog by the month it was due, beside its weekly sibling */}
              {/* CR-7270: this series is TODAY'S open-overdue stock bucketed by
                  the month it was due (charts.py ``openArrearsByDueMonth``,
                  built unconditionally) — not a month-end balance. Its bars do
                  not change with the Historical/Live switch, so its drill must
                  not either: without ``data_mode=live`` the backend answers a
                  past month from that month's frozen snapshot and the modal
                  lists a different cohort from the bar that opened it (a bar of
                  0 opening a modal of 2). The frozen sibling is the Historical
                  Arrears Trend in CostHistoricalView, which DOES follow the
                  switch and passes no override. */}
              {/* DORMANT — not wired; see CR-1421 A2 (Aaron, 15 Oct 2026: "just hide this trash for now").
                  charts/OpenArrearsByDueMonthChart renders charts.py ``openArrearsByDueMonth``, which
                  buckets by due month over the dashboard period; on this tab's forward period it
                  could never hold a point. Restore with the CR-7270 F2 data_mode=live drill. */}

              {/* Charts Row 3 - PPV Section (CR-5456) */}
              {/* Future PPV: committed variance on outstanding quantity, by
                  supplier promise date, with overdue lines as Arrears.
                  Hidden entirely for supplier users (no data from backend). */}
              {ppvData && (
                <PPVChartPanels view={ppvData.future} isFuture={true} drilldownFilters={ppvFutureDrilldownFilters} />
              )}

              {/* Charts Row 5 - Highest Value Suppliers */}
              {/* CR-3848: Added Top 5/10/20 selector */}
              <ChartWrapper
                title="Highest Value Suppliers"
                data={highestValueSuppliers}
                exportFilename="highest-value-suppliers"
                headerActions={
                  <TopNSelector value={topHighestValueSuppliers} onChange={setTopHighestValueSuppliers} />
                }
              >
                {/* CR-5457 rework: drill into the OPEN ORDER BOOK
                    (open_order_value → PurchaseOrderLine, status
                    pending/partially_received), NOT 'spend' which lists
                    goods-received (GRN) rows. Pass the clicked supplier so the
                    drill-down scopes to it. */}
                <RankedBarList
                  items={highestValueRanked}
                  onSelect={(supplierName) => openDrilldown('open_order_value', {
                    // CR-5454: same population as the ranking — the incoming
                    // window, not the dashboard dates on delivery_date.
                    ...incomingDrilldownFilters,
                    supplier_name: supplierName,
                  }, supplierName)}
                />
              </ChartWrapper>
            </div>
          )}

          {activeCostTab === 'historical' && (
            <CostHistoricalView
              data={data}
              dataMode={filters?.dataMode}
              ppvView={ppvData?.historical}
              ppvDrilldownFilters={ppvDrilldownFilters}
              onChartClick={handleChartClick}
              rangeLabel={filters ? (quickRangeLabel(filters) ?? `${formatDate(filters.dateFrom)} – ${formatDate(filters.dateTo)}`) : undefined}
            />
          )}
        </div>
      </div>
    </div>
  )
}
